import axios from 'axios';
import { API_BASE_URL } from '../config';

const getHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const accionesRapidasService = {
  /**
   * Genera el balance del período: ejecuta automatización IVA, genera balance
   * en BD, crea PDF y lo sube a R2. Retorna { success, message, url }.
   */
  async balanceIva(id_company, anual, mes, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/iva/balance`,
        { id_company, anual, mes },
        { headers: getHeaders(token), timeout: 600000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async linkPagoIva(id_company, anual, mes, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/iva/link-pago`,
        { id_company, anual, mes },
        { headers: getHeaders(token), timeout: 600000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async estadoPagoIva(id_company, anual, mes, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/iva/estado-pago`,
        { id_company, anual, mes },
        { headers: getHeaders(token), timeout: 30000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async liquidacionesPeriodo(id_company, anual, mes, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/rrhh/liquidaciones/generar-periodo`,
        { id_company, mes, anio: anual },
        { headers: getHeaders(token), timeout: 300000 }
      );
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      return { success: false, message: msg };
    }
  },

  async estadoPrevired(id_company, anual, mes, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/rrhh/estado-previred`,
        { id_company, anual, mes },
        { headers: getHeaders(token), timeout: 15000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async linkPagoPrevired(id_company, anual, mes, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/generar-link-pago-previred`,
        { id_company, anual, mes },
        { headers: getHeaders(token), timeout: 120000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  },

  async crearPeticion(id_company, tipo, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/peticion`,
        { id_company, tipo },
        { headers: getHeaders(token), timeout: 10000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async obtenerFiniquito(id_company, rut, token) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/rrhh/obtener-finiquito`,
        { id_company, rut },
        { headers: getHeaders(token), timeout: 15000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async cargarCartola(file, id_company, token) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id_company', String(id_company));
      const res = await axios.post(
        `${API_BASE_URL}/api/acciones-rapidas/cartola/cargar-excel`,
        formData,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      return res.data;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },

  async contratoDeTrabajoByRut(id_company, rut, token) {
    try {
      const params = { id_company, estado: 'VIGENTE' };
      if (rut?.trim()) params.rut = rut.trim();
      const res = await axios.get(
        `${API_BASE_URL}/api/rrhh/contratos`,
        { params, headers: getHeaders(token), timeout: 15000 }
      );
      const data = res.data;
      if (!data.success || data.count === 0) {
        const quien = rut?.trim() ? `el RUT ${rut}` : 'la empresa';
        return { success: false, message: `No se encontraron contratos vigentes para ${quien}` };
      }
      const conPdf = data.data.filter(c => c.url_contrato_firmado);
      if (conPdf.length === 0) {
        const nombres = data.data.map(c => `${c.nombres} ${c.apellido_paterno} (${c.rut})`).join(', ');
        return { success: false, message: `Se encontraron ${data.count} contrato(s) vigente(s) pero ninguno tiene PDF adjunto: ${nombres}` };
      }
      const lineas = conPdf.map(c => {
        const nombre = `${c.nombres} ${c.apellido_paterno} ${c.apellido_materno || ''}`.trim();
        const fecha = c.fecha_inicio ? new Date(c.fecha_inicio).toLocaleDateString('es-CL') : '';
        return `${nombre} (${c.rut})${fecha ? ' · Desde: ' + fecha : ''} · PDF: ${c.url_contrato_firmado}`;
      });
      return {
        success: true,
        message: `${conPdf.length} contrato(s) vigente(s): ${lineas.join(' | ')}`,
        url: conPdf[0].url_contrato_firmado,
        urls: conPdf.map(c => c.url_contrato_firmado),
      };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  },
};

export default accionesRapidasService;
